/** @jsx jsx */

import type { ReactNode } from 'react';
import { Fragment, forwardRef } from 'react';
import { jsx } from '@balance-web/core';
import { DefaultTextPropsProvider, Text } from '@balance-web/text';
import { useTheme } from '@balance-web/theme';

import { CheckboxPrimitive } from './CheckboxPrimitive';
import { useCheckboxGroupContext } from './context';
import { getCheckboxGroupItemProps } from './useCheckboxGroupState';
import type { CheckboxProps } from './types';

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ children, ...consumerProps }, ref) => {
    const { spacing } = useTheme();
    const groupState = useCheckboxGroupContext();
    const inputProps = groupState
      ? getCheckboxGroupItemProps(consumerProps, groupState)
      : consumerProps;
    const isSmall = inputProps.size === 'small';
    const hasChildren = children !== undefined && children !== null;

    return (
      <label
        css={
          hasChildren
            ? { alignItems: 'flex-start', display: 'inline-flex' }
            : {
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
              }
        }
      >
        <CheckboxPrimitive ref={ref} {...inputProps} />
        {hasChildren ? (
          <DefaultTextPropsProvider
            color={inputProps.disabled ? 'textDisabled' : undefined}
            leading={isSmall ? 'tight' : undefined}
            size={isSmall ? 'xsmall' : 'small'}
            weight="medium"
          >
            <div
              css={{
                marginLeft: spacing.small,
                paddingTop: spacing.xxsmall,
                userSelect: 'none',
              }}
            >
              <Content>{children}</Content>
            </div>
          </DefaultTextPropsProvider>
        ) : null}
      </label>
    );
  }
);

const Content = ({ children }: { children: ReactNode }) => {
  // Support "naked" strings as children
  if (typeof children === 'string' || typeof children === 'number') {
    return <Text>{children}</Text>;
  }

  // NOTE: fragment resolves TS issue with component return types
  return <Fragment>{children}</Fragment>;
};
